import * as React from "react";

import HelpIcon from "@mui/icons-material/Help";
import {
  Checkbox,
  Fade,
  FormControlLabel,
  FormGroup,
  IconButton,
  Link,
  MenuItem,
  Stack,
  styled,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Controller, UseFormRegister } from "react-hook-form";
import { Control, UseFormResetField } from "react-hook-form/dist/types";

import { FeaturesContext } from "context/featurescontext";
import { featureNames } from "src/domain/administrative/resource-management/features/features/utils/features";
import { compareGPUInstanceTypes, compareInstanceTypes } from "src/domain/core/workflow/utils/nodeSort";
import theme from "theme";

const NodeField = styled(TextField)(() => ({
  width: "100%",
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: theme.palette.error.main,
  },
  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: theme.palette.error.main,
  },
}));

type Params = {
  head_node_instance_types: MachineInstanceType[];
  gpu_head_node_instance_types: MachineInstanceType[];
  compute_node_instance_types: MachineInstanceType[];
  gpu_compute_node_instance_types: MachineInstanceType[];
  template?: Template;
};

interface NodeInputProps {
  resetField: UseFormResetField<any>;
  control: Control<any>;
  params: Params;
  gpuInitial: boolean;
  register: UseFormRegister<any>;
}

export const NodeInput: React.FC<NodeInputProps> = ({ resetField, control, params, register }) => {
  const myFeatures = React.useContext<MyFeatures>(FeaturesContext);

  const gpuFeature: boolean = React.useMemo(() => {
    return myFeatures ? !!myFeatures.find((feature) => feature.name === featureNames.GPU) : false;
  }, [myFeatures]);
  const hasChangedRef = React.useRef(false);

  //Given an instance type like 'c4.2xlarge' return the display name
  const findNameByInstanceType = (instanceList: MachineInstanceTypeDesc[], instanceType: string): string => {
    return instanceList?.find((instance) => instance.instance_type === instanceType)?.name;
  };

  const handleGPUChange = () => {
    hasChangedRef.current = true;
    resetSelectValues(!gpuSelected);
    setGPUSelected(!gpuSelected);
  };

  // Collect all the CPU Head Nodes and Sort them
  const allCPUHeadNodes = React.useMemo(() => {
    return params?.head_node_instance_types?.sort(compareInstanceTypes);
  }, [params.head_node_instance_types]);

  // Collect all the GPU Head Nodes and Sort them
  const allGPUHeadNodes = React.useMemo(() => {
    return params?.gpu_head_node_instance_types?.sort(compareGPUInstanceTypes);
  }, [params.gpu_head_node_instance_types]);

  // Collect and Sort CPU Compute Nodes
  const allCPUComputeNodes = React.useMemo(() => {
    return params?.compute_node_instance_types?.sort(compareInstanceTypes);
  }, [params.compute_node_instance_types]);

  // Collect and Sort GPU Compute Nodes
  const allGPUComputeNodes = React.useMemo(() => {
    return params?.gpu_compute_node_instance_types?.sort(compareGPUInstanceTypes);
  }, [params.gpu_compute_node_instance_types]);

  const getDefaultNodes = (type: string) => {
    const isGPU = params?.template?.body?.isGPU;

    if (type === "head") {
      return isGPU ? allGPUHeadNodes : allCPUHeadNodes;
    } else {
      //type === "compute"
      return isGPU ? allGPUComputeNodes : allCPUComputeNodes;
    }
  };

  // computeNodes and headNodes hold the list of nodes regardless of cpu/gpu
  const [computeNodes, setComputeNodes] = React.useState(() => getDefaultNodes("compute"));
  const [headNodes, setHeadNodes] = React.useState(() => getDefaultNodes("head"));
  // Whether the user has selected GPU Enabled, this is in the grandparent so the size of the dialog can be dynamic for GPU Name sizes
  const [gpuSelected, setGPUSelected] = React.useState(params?.template?.body?.isGPU || false);

  const getNodeDefaultValue = React.useCallback(
    (
      newGPU: boolean,
      templateInstanceType: string,
      gpuNodes: MachineInstanceType[],
      cpuNodes: MachineInstanceType[]
    ) => {
      if (!params?.template) {
        // If no template, return the default value based on isGPU (which is just the first [which is the cheapest])
        return newGPU ? gpuNodes[0]?.name : cpuNodes[0]?.name;
      }

      // From here on we have a template, so lets use its body for pertinent info
      const { body } = params.template;

      if (newGPU) {
        // Basically if we are switching GPU Enabled back in line with the template, we want to use the template default, otherwise we want to use just the first/cheapest one
        return body?.isGPU ? findNameByInstanceType(gpuNodes, templateInstanceType) : gpuNodes[0]?.name;
      }

      // Switching back to CPU -- check if the template is CPU-based, otherwise we want to use just the first/cheapest one
      return !body.isGPU ? findNameByInstanceType(cpuNodes, templateInstanceType) : cpuNodes[0]?.name;
    },
    [params.template]
  );

  const gpuToolTipText = (
    <div>
      <p>
        GPU processing can significantly speed up complex computations, handle large datasets, and achieve faster
        results.
        <br />
        <br />
        GPU processing can be more expensive, requires specialized programming, and is not recommended for smaller or
        less complex tasks.
        <br />
        <br />
        <Link
          href={process.env.NEXT_PUBLIC_NODE_LEARN_MORE_URL}
          color="inherit"
          underline="none"
          fontWeight="bold"
          target="_blank"
        >
          LEARN MORE
        </Link>
      </p>
    </div>
  );

  const resetSelectValues = React.useCallback(
    (isGPU: boolean) => {
      const headNodeDefault = getNodeDefaultValue(
        isGPU,
        params?.template?.body?.headNodeInstanceType,
        allGPUHeadNodes,
        allCPUHeadNodes
      );
      const computeNodeDefault = getNodeDefaultValue(
        isGPU,
        params?.template?.body?.computeNodeInstanceType,
        allGPUComputeNodes,
        allCPUComputeNodes
      );

      // Clear Selection
      resetField("head_node_instance_type");
      resetField("compute_node_instance_type");

      // Update Values
      if (isGPU) {
        setHeadNodes(allGPUHeadNodes);
        setComputeNodes(allGPUComputeNodes);
      } else {
        setHeadNodes(allCPUHeadNodes);
        setComputeNodes(allCPUComputeNodes);
      }

      // Update Selections
      resetField("head_node_instance_type", { defaultValue: headNodeDefault });
      resetField("compute_node_instance_type", { defaultValue: computeNodeDefault });
    },
    [
      getNodeDefaultValue,
      params?.template?.body?.headNodeInstanceType,
      params?.template?.body?.computeNodeInstanceType,
      allGPUHeadNodes,
      allCPUHeadNodes,
      allGPUComputeNodes,
      allCPUComputeNodes,
      resetField,
    ]
  );

  return (
    <Stack spacing={7} sx={{ mb: 5 }}>
      {gpuFeature && (
        <Stack direction="row" spacing={0}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  id="is_gpu"
                  checked={gpuSelected}
                  data-testid="is_gpu"
                  {...register("is_gpu", {
                    onChange: () => {
                      handleGPUChange();
                    },
                  })}
                />
              }
              label="GPU Enabled"
              sx={{ mr: 0 }}
            />
          </FormGroup>
          <Tooltip title={gpuToolTipText} TransitionComponent={Fade} arrow placement="top">
            <IconButton size="small" sx={{ maxHeight: "34px", mt: 1 }}>
              <HelpIcon
                data-testid={"gpu-helpicon"}
                sx={{
                  fontSize: "1.25rem",
                }}
              />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
      <Controller
        control={control}
        name={"head_node_instance_type"}
        defaultValue={() =>
          getNodeDefaultValue(
            !!params?.template?.body?.isGPU,
            params?.template?.body?.headNodeInstanceType,
            allGPUHeadNodes,
            allCPUHeadNodes
          )
        }
        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => {
          return (
            <NodeField
              select
              id="head-node-select"
              label={getNodeLabel("Head", gpuSelected, headNodes?.length > 0)}
              helperText={getNodeHelperText("head", error?.message, headNodes?.length > 0)}
              disabled={headNodes?.length === 0}
              onChange={onChange}
              onBlur={onBlur}
              value={value}
              error={error && true}
            >
              {headNodes?.length > 0 ? (
                headNodes.map((option) => (
                  <MenuItem key={option.instance_type} value={option.name}>
                    <Typography variant="body2">{option.name}</Typography>
                  </MenuItem>
                ))
              ) : (
                <MenuItem key="" value={gpuSelected ? "No GPU Instances Available" : "No instances configured"}>
                  <Typography variant="body2">
                    {gpuSelected ? "No GPU Instances Available" : "No instances configured"}
                  </Typography>
                </MenuItem>
              )}
            </NodeField>
          );
        }}
      />
      <Controller
        control={control}
        name="compute_node_instance_type"
        defaultValue={() =>
          getNodeDefaultValue(
            !!params?.template?.body?.isGPU,
            params?.template?.body?.computeNodeInstanceType,
            allGPUComputeNodes,
            allCPUComputeNodes
          )
        }
        render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => {
          return (
            <NodeField
              select
              fullWidth
              id="compute-node-select"
              label={getNodeLabel("Compute", gpuSelected, computeNodes?.length > 0)}
              helperText={getNodeHelperText("compute", error?.message, computeNodes?.length > 0)}
              disabled={computeNodes?.length === 0}
              onChange={onChange}
              onBlur={onBlur}
              value={value}
              error={error && true}
            >
              {computeNodes?.length > 0 ? (
                computeNodes.map((option) => (
                  <MenuItem key={option.instance_type} value={option.name}>
                    <Typography variant="body2">{option.name}</Typography>
                  </MenuItem>
                ))
              ) : (
                <MenuItem key="" value={gpuSelected ? "No GPU Instances Available" : "No instances configured"}>
                  <Typography variant="body2">
                    {gpuSelected ? "No GPU Instances Available" : "No instances configured"}
                  </Typography>
                </MenuItem>
              )}
            </NodeField>
          );
        }}
      />
    </Stack>
  );
};

function getNodeLabel(type: string, gpuSelected: boolean, nodesExist: boolean) {
  const label = gpuSelected ? `GPU ${type} Node *` : `${type} Node *`;

  return nodesExist ? label : <span style={{ color: theme.palette.error.main }}>{label}</span>;
}

function getNodeHelperText(type: string, errorText: string, nodesExist: boolean) {
  // No Nodes Exist
  if (!nodesExist) {
    return (
      <Typography variant="caption" color="error">
        No Instances Available For Your Region
      </Typography>
    );
  }

  // Nodes Exist
  return errorText ? errorText : `Select the size of your ${type} node`;
}
